import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import apiService from '../services/api'
import { useRecaptcha } from '../hooks/useRecaptcha'

const AuthRegister = () => {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [first_name, setFirstName] = useState('')
  const [last_name, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [errors, setErrors] = useState({})
  const [type, setType] = useState('person')
  const [company_name, setCompanyName] = useState('')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { recaptchaRef, recaptchaToken, isEnabled: recaptchaEnabled, resetRecaptcha } = useRecaptcha()

  useEffect(() => {
    const isMobile = window.innerWidth < 768
    if (isMobile) {
      document.body.style.overflow = 'hidden'
      document.body.style.height = '100vh'
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const nextErrors = {}
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      nextErrors.email = 'Email invalide'
    }
    if (!password || password.length < 6) {
      nextErrors.password = 'Mot de passe trop court (min 6)'
    }
    if (first_name.trim().length > 19) {
      nextErrors.first_name = 'Prénom trop long'
    }
    if (last_name.trim().length > 19) {
      nextErrors.last_name = 'Nom trop long'
    }
    if (recaptchaEnabled && !recaptchaToken) {
      toast.error('Veuillez compléter la vérification reCAPTCHA')
      setLoading(false)
      return
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      setLoading(false)
      return
    }

    try {
      setErrors({})
      const payload = { email, password, first_name, last_name, phone, recaptchaToken }
      if (username.trim()) payload.username = username.trim()
      if (type === 'company') payload.company_name = company_name
      const res = await apiService.post('/auth/register', payload)
      localStorage.setItem('user_token', res.data.token)
      const redirect = params.get('redirect') || '/account'
      navigate(redirect)
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur inscription')
      resetRecaptcha()
      const details = err.response?.data?.details
      if (Array.isArray(details)) {
        const map = {}
        for (const d of details) {
          const k = d.param || d.path
          map[k] = d.msg || d.message || 'Champ invalide'
        }
        setErrors(map)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="h-screen bg-white md:bg-gray-100 flex flex-col md:items-center md:justify-center px-4 pt-20 pb-4 md:p-6 overflow-hidden md:min-h-screen">
      <div className="bg-white border-0 md:border border-gray-200 rounded-xl p-6 shadow-none md:shadow-lg w-full max-w-md">
        <h1 className="text-2xl font-bold mb-2 text-center">Créer un compte</h1>
        <div className="mb-4">
          <div className="flex items-center gap-6 text-sm text-gray-700">
            <label className="inline-flex items-center">
              <input type="radio" name="account_type" className="mr-2" checked={type==='person'} onChange={()=>setType('person')} />
              Particulier
            </label>
            <label className="inline-flex items-center">
              <input type="radio" name="account_type" className="mr-2" checked={type==='company'} onChange={()=>setType('company')} />
              Société
            </label>
          </div>
        </div>
        {error && (
          <div className="mb-4">
            <p className="text-red-600 text-sm mb-3">{error}</p>
            {(error.includes('Email') || error.includes('pseudo')) && (
              <div className="flex gap-2">
                <Link to="/login" className="btn-outline">Connexion</Link>
                <Link to="/login?forgot=1" className="btn-outline">Mot de passe oublié</Link>
              </div>
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
              <input className="input-field" value={first_name} onChange={e => setFirstName(e.target.value)} maxLength={50} />
              {errors.first_name && <p className="text-red-600 text-xs mt-1">{errors.first_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
              <input className="input-field" value={last_name} onChange={e => setLastName(e.target.value)} maxLength={50} />
              {errors.last_name && <p className="text-red-600 text-xs mt-1">{errors.last_name}</p>}
            </div>
          </div>
          {type === 'company' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nom de l'entreprise</label>
              <input className="input-field" value={company_name} onChange={e => setCompanyName(e.target.value)} maxLength={50} />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone <span className="text-red-500">*</span></label>
            <input className="input-field" value={phone} onChange={e => setPhone(e.target.value)} placeholder="06 12 34 56 78" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input type="email" className="input-field" value={email} onChange={e => setEmail(e.target.value)} />
            {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe</label>
            <input type="password" className="input-field" value={password} onChange={e => setPassword(e.target.value)} />
            {errors.password && <p className="text-red-600 text-xs mt-1">{errors.password}</p>}
          </div>
          {recaptchaEnabled && (
            <div className="flex justify-center">
              <div ref={recaptchaRef} />
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={loading}>{loading ? 'Création...' : 'Créer le compte'}</button>
        </form>
        <p className="text-sm text-gray-600 mt-4 text-center">
          Déjà un compte ? <Link to="/login" className="text-orange-600 hover:underline">Se connecter</Link>
        </p>
      </div>
    </div>
  )
}

export default AuthRegister
